import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/Layout";
import SectorBenchmarks from "@/components/SectorBenchmarks";
import { toast } from "sonner";
import * as Icons from "lucide-react";

function Icon({ name, className }) { const C = Icons[name] || Icons.Circle; return <C className={className} />; }

export default function Dashboard() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [checks, setChecks] = useState([]);
  const [company, setCompany] = useState(null);
  const [sector, setSector] = useState(null);

  useEffect(() => {
    api.get("/services").then(r => setServices(r.data)).catch(() => {});
    api.get("/business-checks/mine").then(r => setChecks(r.data)).catch(() => {});
    api.get("/companies/mine").then(r => setCompany(r.data.company_id ? r.data : null)).catch(() => {});
  }, []);

  const [portalLoading, setPortalLoading] = useState(false);
  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { return_url: window.location.origin + "/dashboard" });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Nessun abbonamento attivo"); }
    setPortalLoading(false);
  };

  const lastCheck = checks[0];
  useEffect(() => {
    if (lastCheck?.sector_slug) api.get(`/sectors/${lastCheck.sector_slug}`).then(r => setSector(r.data)).catch(() => {});
  }, [lastCheck?.sector_slug]);

  const cats = { diagnosi: "Diagnostica & Controllo", simulazione: "Simulatori & Decisioni", crescita: "Pianificazione & AI Advisor" };
  const planLabel = (company?.current_plan || user?.plan || "free").toUpperCase();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#FF6A24] font-bold">Ciao {user?.name || "imprenditore"} · Piano {planLabel}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">La tua cabina di regia</h1>
          <p className="text-sm text-[#7A7788] mt-1" data-testid="dash-company">{company ? `Azienda: ${company.trade_name || company.legal_name || "registrata"}` : "Nessuna azienda collegata"}</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && <Link to="/admin" className="ic-btn-ghost" data-testid="dashboard-admin">Pannello Admin</Link>}
          {lastCheck && <Link to="/report" className="ic-btn-ghost" data-testid="dashboard-report">Report completo</Link>}
          {(company?.current_plan || user?.plan) && (company?.current_plan || user?.plan) !== "free" && (
            <button onClick={openPortal} disabled={portalLoading} className="ic-btn-ghost" data-testid="dashboard-portal">
              {portalLoading ? "Apertura…" : "Gestisci abbonamento"}
            </button>
          )}
          <Link to="/business-check" className="ic-btn-primary" data-testid="dashboard-new-check">Nuovo Business Check</Link>
        </div>
      </div>

      {/* Score / Potenziale / Criticità */}
      <div className="grid md:grid-cols-3 gap-5 mb-10">
        <div className="ic-card p-6" data-testid="dash-score">
          <p className="text-xs uppercase tracking-widest text-[#7A7788]">ImpresaChiara Score</p>
          {lastCheck ? (
            <><p className="text-5xl font-extrabold text-[#5424D5] mt-2">{lastCheck.score}<span className="text-xl text-[#7A7788]">/100</span></p>
            <p className="text-xs text-[#7A7788] mt-1">Aggiornato il {lastCheck.created_at?.slice(0, 10)}</p></>
          ) : (
            <><p className="text-2xl font-bold text-[#7A7788] mt-3">Nessun check</p>
            <Link to="/business-check" className="text-sm text-[#5424D5] font-semibold mt-2 inline-block" data-testid="dash-start-check">{company ? "Avvia il Business Check →" : "Crea la tua azienda →"}</Link></>
          )}
        </div>
        <div className="ic-card p-6" data-testid="dash-improvement">
          <p className="text-xs uppercase tracking-widest text-[#7A7788]">Potenziale di miglioramento</p>
          <p className="text-5xl font-extrabold text-[#FF6A24] mt-2">{lastCheck ? `€${lastCheck.improvement_potential?.toLocaleString('it-IT')}` : "—"}</p>
          <p className="text-[10px] text-[#7A7788] mt-2">Stima indicativa — non un risultato garantito.</p>
        </div>
        <div className="ic-card p-6" data-testid="dash-critical">
          <p className="text-xs uppercase tracking-widest text-[#7A7788]">Principale criticità</p>
          <p className="text-lg font-semibold mt-3">{lastCheck?.insights?.[0]?.title || "Completa un Business Check per scoprirla"}</p>
          <p className="text-xs text-[#7A7788] mt-1">{lastCheck ? "Quest'area incide maggiormente sul tuo risultato." : ""}</p>
        </div>
      </div>

      {/* Benchmark di settore reali (BACH) */}
      {lastCheck?.sector_slug && (
        <div className="mb-10">
          <h2 className="text-xl font-bold mb-4">I benchmark del tuo settore</h2>
          <SectorBenchmarks slug={lastCheck.sector_slug} compact />
        </div>
      )}

      {/* Accesso rapido */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4">Accesso rapido</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[{ to: "/report", l: "Report", id: "report" }, { to: "/cfo-simulator", l: "CFO Simulator", id: "cfo" },
            { to: "/report#opportunita", l: "Opportunity", id: "opp" }, { to: "/report#obiettivi", l: "Goals", id: "goals" },
            { to: "/impostazioni", l: "Impostazioni", id: "settings" }].map(x => (
            <Link key={x.id} to={x.to} className="ic-card p-4 text-center font-semibold text-sm hover:border-[#5424D5] transition" data-testid={`quick-${x.id}`}>{x.l}</Link>
          ))}
        </div>
      </div>

      {/* Come guadagno €1.000 in più */}
      <div className="ic-card p-8 mb-10 border-[#FF6A24]/40 bg-gradient-to-br from-white to-[#FFF0E8]" data-testid="dash-earn1000">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-xl">
            <span className="ic-badge status-coming mb-2">Coming Soon · Piano GROW</span>
            <h3 className="text-2xl font-bold">Come posso guadagnare €1.000 in più al mese?</h3>
            <p className="text-[#524F5E] mt-2 text-sm">ImpresaChiara analizzerà le leve disponibili nella tua attività e simulerà quali azioni possono avere il maggiore impatto.</p>
          </div>
          <Icons.Target className="w-14 h-14 text-[#FF6A24]" />
        </div>
      </div>

      {/* Storico */}
      {checks.length > 0 && (
        <div className="mb-10">
          <h2 className="text-xl font-bold mb-4">Storico Business Check</h2>
          <div className="ic-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F7]"><tr><th className="text-left p-3">Data</th><th className="text-left p-3">Settore</th><th className="text-left p-3">Score</th></tr></thead>
              <tbody>
                {checks.map((c, i) => (
                  <tr key={i} className="border-t border-[#E5E4EB]">
                    <td className="p-3">{c.created_at?.slice(0, 10)}</td>
                    <td className="p-3">{c.sector_slug}</td>
                    <td className="p-3 font-bold text-[#5424D5]">{c.score}/100</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Strumenti */}
      {Object.keys(cats).map(catId => (
        <div key={catId} className="mb-10">
          <h2 className="text-xl font-bold mb-4">{cats[catId]}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.filter(s => s.category === catId).map(s => (
              <div key={s.service_id} className="ic-card p-6" data-testid={`tool-${s.service_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-11 h-11 rounded-xl bg-[#EDE8FC] text-[#5424D5] flex items-center justify-center"><Icon name={s.icon} className="w-5 h-5" /></div>
                  <StatusBadge status={s.status} />
                </div>
                <h3 className="font-semibold text-lg">{s.service_name}</h3>
                <p className="text-sm text-[#524F5E] mt-1">{s.description}</p>
                {s.status === "ACTIVE" ? (
                  <Link to={`/${s.slug}`} className="ic-btn-primary text-sm inline-block mt-4">Apri</Link>
                ) : (
                  <Link to={`/${s.slug}`} className="ic-btn-ghost text-sm inline-block mt-4">{s.status === "COMING_SOON" ? "In lista d'attesa" : "Sblocca"}</Link>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
